import React, { useState } from "react";
import BaseTable from "../components/shared/BaseTable";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { EHRStatusReport, RequestStatusReport } from "../api/Reports";
import api from "../api/config";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import StyledTextField from "../components/shared/StyledTextField";
import * as XLXS from "xlsx";
import IOSSwitch from "../components/shared/IOSSwitch";

const Reports = () => {
  const [reportTemplates, setReportTemplates] = useState([
    {
      id: 1,
      name: "EHR Status Report",
      columns: [
        { id: "ehr_status", label: "EHR Status" },
        { id: "count", label: "Count" },
        { id: "date", label: "Date" },
        { id: "total", label: "Total" },
      ],
    },
    {
      id: 2,
      name: "Request Status Report",
      columns: [
        { id: "AppointmentDate", label: "Appt. Date" },
        { id: "DataRecieved", label: "Data Recieved" },
        { id: "DataUploaded", label: "Data Uploaded" },
        { id: "DateAndTimeRequest", label: "Date and Time Request" },
        { id: "ImagingStudyRecieved", label: "Imaging Study Recieved" },
        { id: "QueryInitiated", label: "Query Initiated" },
        { id: "ReportRecieved", label: "Report Recieved" },
        { id: "RequestWasSent", label: "Request Was Sent" },
        { id: "patientDOB", label: "DOB" },
        { id: "patientFirstName", label: "First Name" },
        { id: "patientGender", label: "Gender" },
        { id: "patientLastName", label: "Last Name" },
        { id: "reasonForFailure", label: "Reason For Failure" },
        { id: "siteName", label: "Site Name" },
      ],
    },
  ]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [dailyReportDate, setDailyReportDate] = useState(dayjs());
  const [reportLoading, setReportLoading] = useState(false);
  const [requestDate, setRequestDate] = useState(dayjs());
  const [expandableRows, setExpandableRows] = useState([]);
  const [toggle, setToggle] = useState(false);

  const formatDate = (date) => {
    return date.format("YYYYMMDD");
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
  };
  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
  };
  const handleDailyReportDateChange = (newValue) => {
    setDailyReportDate(newValue);
  };

  const toggleSwitch = () => {
    setToggle(!toggle);
  };

  const handleRequestDateChange = (newValue) => {
    setRequestDate(newValue);
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      const clientUuid = localStorage.getItem("clientuuid");

      if (selectedReport.id === 1) {
        const params = {};

        if (toggle) {
          params.start_date = formatDate(startDate);
          params.end_date = formatDate(endDate);
        } else {
          params.start_date = formatDate(dailyReportDate);
          params.end_date = formatDate(dailyReportDate);
        }

        const response = await api.get(`${EHRStatusReport}/${clientUuid}`, {
          params,
        });

        const processedData = response.data.flatMap((entry) =>
          Object.entries(entry.ehr_status).map(([key, status]) => {
            const extras = entry.extras?.[status] || {};

            return {
              ehr_status: status,
              count: entry.count[key] || "0",
              date: entry.date,
              total: entry.total || "0",
              id: `${entry.date}-${status}`,
              extras,
            };
          })
        );
        setTableData(processedData);

        const expandableRowIds = processedData
          .filter((row) => row.extras && Object.keys(row.extras).length > 0)
          .map((row) => row.id);
        setExpandableRows(expandableRowIds);
      } else if (selectedReport.id === 2) {
        const response = await api.get(`${RequestStatusReport}/${clientUuid}`, {
          params: {
            request_date: formatDate(requestDate),
          },
        });
        console.log("Request Status Report response:", response.data);

        const processedData = response.data.map((entry, index) => ({
          ...entry,
          id: `${index}`,
        }));

        console.log("Processed data:", processedData);
        setExpandableRows([]);
        setTableData(processedData);
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setReportLoading(false);
    }
  };

  const exportToCSV = () => {
    if (tableData.length === 0) return;

    let data = [];

    if (selectedReport.id === 1) {
      data = tableData.map(({ id, ...row }) => ({
        "EHR Status": row.ehr_status,
        Count: row.count,
        Date: row.date,
        Total: row.total,
      }));

      const expandedData = [];
      tableData.forEach((row) => {
        expandedData.push(row);

        const expandableRow = tableData.find((item) => item.id === row.id);
        if (expandableRow && expandableRow.extras) {
          Object.entries(expandableRow.extras).forEach(([key, value]) => {
            expandedData.push({
              "Subrow Key": key,
              "Subrow Value": value,
            });
          });
        }
      });

      data = expandedData;
    } else if (selectedReport.id === 2) {
      data = tableData.map(({ id, ...row }) => ({
        "Appointment Date": row.AppointmentDate,
        "Data Recieved": row.DataRecieved,
        "Data Uploaded": row.DataUploaded,
        "Date And Time Request": row.DateAndTimeRequest,
        "Imaging Study Recieved": row.ImagingStudyRecieved,
        "Query Initiated": row.QueryInitiated,
        "Report Recieved": row.ReportRecieved,
        "Request Was Sent": row.RequestWasSent,
        "Patient DOB": row.patientDOB,
        "Patient First Name": row.patientFirstName,
        "Patient Gender": row.patientGender,
        "Patient Last Name": row.patientLastName,
        "Reason For Failure": row.reasonForFailure,
        "Site Name": row.siteName,
      }));
    }

    const worksheet = XLXS.utils.json_to_sheet(data, {
      header: Object.keys(data[0]),
      skipHeader: false,
    });

    const columnWidths = Object.keys(data[0]).map((key) => ({
      wch: Math.max(
        ...data.map((row) => (row[key] ? row[key].toString().length : 10)),
        key.length
      ),
    }));

    worksheet["!cols"] = columnWidths;

    if (selectedReport.id === 1) {
      data.forEach((row, index) => {
        if (row["Subrow Key"]) {
          const rowIndex = index + 2;
          Object.keys(row).forEach((key, cellIndex) => {
            const cell =
              worksheet[XLXS.utils.encode_cell({ r: rowIndex, c: cellIndex })];
            if (cell) {
              cell.s = { fill: { fgColor: { rgb: "D3D3D3" } } };
            }
          });
        }
      });
    }

    const workbook = XLXS.utils.book_new();
    XLXS.utils.book_append_sheet(workbook, worksheet, "Report");

    const excelBuffer = XLXS.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = dayjs().format("YYYYMMDD");
    link.href = url;
    link.setAttribute("download", `${selectedReport.name}-${timestamp}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
      }}
    >
      <Box sx={{ width: "230px", borderRight: "1px solid #ddd", padding: 2 }}>
        <List>
          {reportTemplates.map((report) => (
            <>
              <ListItem key={report.id} disablePadding>
                <ListItemButton
                  onClick={() => {
                    setSelectedReport(report);
                    setTableData([]);
                  }}
                  sx={{
                    color:
                      selectedReport?.id === report.id
                        ? "rgb(165, 224, 255)"
                        : "",
                  }}
                >
                  <ListItemText primary={report.name} />
                </ListItemButton>
              </ListItem>
              <hr />
            </>
          ))}
        </List>
      </Box>
      <Box sx={{ paddingLeft: 2, width: "100%" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          {selectedReport && selectedReport.id === 1 && (
            <div>
              <div
                style={{ display: "flex", gap: "20px", marginBottom: "10px" }}
              >
                <Typography style={{ marginTop: "auto", marginBottom: "auto" }}>
                  Daily Report
                </Typography>
                <IOSSwitch checked={toggle} onChange={toggleSwitch} />
                <Typography style={{ marginTop: "auto", marginBottom: "auto" }}>
                  Range Report
                </Typography>
              </div>

              <div style={{ display: "flex", gap: "20px" }}>
                {toggle ? (
                  <div style={{ display: "flex", gap: "20px" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => handleStartDateChange(newValue)}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            label="Start Date"
                            variant="outlined"
                            fullWidth
                            sx={{
                              backgroundColor: "black",
                            }}
                          />
                        )}
                        slotProps={{
                          layout: {
                            sx: {
                              color: "#000000",
                              backgroundColor: "#3E526A",
                            },
                          },
                        }}
                        sx={{
                          width: "180px",
                          "& .MuiPickersLayout-root": {
                            backgroundColor: "#141b25 !important",
                          },
                          "& .MuiInputLabel-root": {
                            color: "#ffffff",
                            borderRadius: "12px",
                            borderColor: "#ffffff",
                            fontSize: "14px",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#ffffff",
                            borderColor: "#F0F5F7",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& > fieldset": { borderColor: "#ffffff" },
                            "&:hover > fieldset": { borderColor: "#ffffff" },
                            "&.Mui-focused > fieldset": {
                              borderColor: "#ffffff",
                            },
                          },
                          "& .MuiSvgIcon-root": {
                            fill: "white !important",
                          },
                          "& .MuiPaper-root": {
                            backgroundColor: "#141b25",
                          },
                        }}
                      />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => handleEndDateChange(newValue)}
                        renderInput={(params) => (
                          <StyledTextField
                            {...params}
                            label="End Date"
                            variant="outlined"
                            fullWidth
                            sx={{
                              backgroundColor: "black",
                            }}
                          />
                        )}
                        slotProps={{
                          layout: {
                            sx: {
                              color: "#000000",
                              backgroundColor: "#3E526A",
                            },
                          },
                        }}
                        sx={{
                          width: "180px",
                          "& .MuiPickersLayout-root": {
                            backgroundColor: "#141b25 !important",
                          },
                          "& .MuiInputLabel-root": {
                            color: "#ffffff",
                            borderRadius: "12px",
                            borderColor: "#ffffff",
                            fontSize: "14px",
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "#ffffff",
                            borderColor: "#F0F5F7",
                          },
                          "& .MuiOutlinedInput-root": {
                            "& > fieldset": { borderColor: "#ffffff" },
                            "&:hover > fieldset": { borderColor: "#ffffff" },
                            "&.Mui-focused > fieldset": {
                              borderColor: "#ffffff",
                            },
                          },
                          "& .MuiSvgIcon-root": {
                            fill: "white !important",
                          },
                          "& .MuiPaper-root": {
                            backgroundColor: "#141b25",
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                ) : (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Daily Report Date"
                      value={endDate}
                      onChange={(newValue) =>
                        handleDailyReportDateChange(newValue)
                      }
                      renderInput={(params) => (
                        <StyledTextField
                          {...params}
                          label="Daily Report Date"
                          variant="outlined"
                          fullWidth
                          sx={{
                            backgroundColor: "black",
                          }}
                        />
                      )}
                      slotProps={{
                        layout: {
                          sx: {
                            color: "#000000",
                            backgroundColor: "#3E526A",
                          },
                        },
                      }}
                      sx={{
                        width: "180px",
                        "& .MuiPickersLayout-root": {
                          backgroundColor: "#141b25 !important",
                        },
                        "& .MuiInputLabel-root": {
                          color: "#ffffff",
                          borderRadius: "12px",
                          borderColor: "#ffffff",
                          fontSize: "14px",
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: "#ffffff",
                          borderColor: "#F0F5F7",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& > fieldset": { borderColor: "#ffffff" },
                          "&:hover > fieldset": { borderColor: "#ffffff" },
                          "&.Mui-focused > fieldset": {
                            borderColor: "#ffffff",
                          },
                        },
                        "& .MuiSvgIcon-root": {
                          fill: "white !important",
                        },
                        "& .MuiPaper-root": {
                          backgroundColor: "#141b25",
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              </div>
            </div>
          )}
          {selectedReport && selectedReport.id === 2 && (
            <div style={{ display: "flex", gap: "20px", paddingTop: "10px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Request Date"
                  value={endDate}
                  onChange={(newValue) => handleRequestDateChange(newValue)}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      label="Request Date"
                      variant="outlined"
                      fullWidth
                      sx={{
                        backgroundColor: "black",
                      }}
                    />
                  )}
                  slotProps={{
                    layout: {
                      sx: {
                        color: "#000000",
                        backgroundColor: "#3E526A",
                      },
                    },
                  }}
                  sx={{
                    width: "180px",
                    "& .MuiPickersLayout-root": {
                      backgroundColor: "#141b25 !important",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#ffffff",
                      borderRadius: "12px",
                      borderColor: "#ffffff",
                      fontSize: "14px",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#ffffff",
                      borderColor: "#F0F5F7",
                    },
                    "& .MuiOutlinedInput-root": {
                      "& > fieldset": { borderColor: "#ffffff" },
                      "&:hover > fieldset": { borderColor: "#ffffff" },
                      "&.Mui-focused > fieldset": { borderColor: "#ffffff" },
                    },
                    "& .MuiSvgIcon-root": {
                      fill: "white !important",
                    },
                    "& .MuiPaper-root": {
                      backgroundColor: "#141b25",
                    },
                  }}
                />
              </LocalizationProvider>
            </div>
          )}

          <div style={{ display: "flex", gap: "20px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateReport}
              sx={{ mt: 2 }}
            >
              Generate Report
            </Button>
            {selectedReport && (
              <Button
                variant="contained"
                color="primary"
                onClick={exportToCSV}
                sx={{ mt: 2 }}
              >
                Export as CSV
              </Button>
            )}
          </div>
        </div>
        {selectedReport && (
          <div style={{ marginTop: "15px" }}>
            <BaseTable
              title={selectedReport.name}
              columns={selectedReport.columns}
              data={tableData}
              isLoading={reportLoading}
              onSearch={() => {
                console.log("searching");
              }}
              noSearch
              onRefresh={generateReport}
              totalRecords={tableData.length}
              limit={tableData.length}
              width={"calc(100vw - 595px)"}
              expandableRows={expandableRows}
              getExpandableRowContent={(rowId) => {
                const row = tableData.find((row) => row.id === rowId);
                if (row && row.extras) {
                  return Object.entries(row.extras).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell>{key}</TableCell>
                      <TableCell>{value}</TableCell>
                    </TableRow>
                  ));
                }
                return null;
              }}
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

export default Reports;
